'use client';

import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { useMenuContext } from 'app/[lang]/_contexts/MenuContext';
import { GeneratedContent, GeneratedContentPage, Scalars } from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { useContentGenerationDelete } from 'providers/ContentGenerationProviders/useContentGenerationDelete';
import { useContentGenerationNodesCount } from 'providers/ContentGenerationProviders/useContentGenerationNodesCount';
import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { Control } from 'react-hook-form';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { Option } from 'types/Option';
import { TGenericError } from 'types/TGenericError';
import useSearchParamsContentGeneration, {
  FiltersContentGenerationForm
} from './useSearchParamsContentGeneration';

type TContentGenerationListContext = {
  deferredSearchQuery?: string;
  searchQuery?: string;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  data?: GeneratedContentPage;
  contentId: string;
  nodesCount: number | undefined;
  refetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<GeneratedContentPage, TGenericError>>;
  isLoading: boolean;
  hasNodes: Scalars['Boolean'];
  isChecking: boolean;
  isSelectDisabled: boolean;
  copyGeneratedContent: Partial<GeneratedContent>;
  setCopyGeneratedContent: Dispatch<SetStateAction<Partial<GeneratedContent>>>;
  handleOpenEditContentGenerationModal: (contentId: string) => void;
  handleOpenDeleteContentGenerationModal: (contentId: string) => void;
  handleCloseDeleteContentGenerationModal: () => void;
  handleDeleteContentGeneration: () => void;
  modalDeleteContentGenerationOpen: boolean;
  optionsSortAttribute: Option[];
  optionsSortOrder: Option[];
  optionsLanguages: Option[];
  optionsContentType: Option[];
  optionsContentFormat: Option[];
  control: Control<FiltersContentGenerationForm>;
  handleClear: () => void;
  isContentFormatFilterDisabled: boolean;
};

const initialState = {
  deferredSearchQuery: '',
  searchQuery: '',
  contentId: '',
  nodesCount: 0,
  page: 1,
  setPage: () => {},
  data: {} as GeneratedContentPage,
  refetch: (() => {}) as (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<GeneratedContentPage, TGenericError>>,
  isLoading: true,
  hasNodes: false,
  isChecking: true,
  isSelectDisabled: false,
  copyGeneratedContent: {} as Partial<GeneratedContent>,
  setCopyGeneratedContent: () => {},
  handleOpenDeleteContentGenerationModal: () => {},
  handleOpenEditContentGenerationModal: () => {},
  handleCloseDeleteContentGenerationModal: () => {},
  handleDeleteContentGeneration: () => {},
  handleClear: () => {},
  modalDeleteContentGenerationOpen: true,
  optionsSortAttribute: [],
  optionsSortOrder: [],
  optionsLanguages: [],
  optionsContentType: [],
  optionsContentFormat: [],
  control: {} as Control<FiltersContentGenerationForm>,
  isContentFormatFilterDisabled: false
};

const ContentGenerationListContext = createContext<TContentGenerationListContext>(initialState);

const useContentGenerationListContext = (): TContentGenerationListContext =>
  useContext(ContentGenerationListContext);

const ContentGenerationListProvider: FC<PropsWithChildren<unknown>> = ({
  children
}): JSX.Element => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const { handleScrollTop } = useMenuContext();

  const [copyGeneratedContent, setCopyGeneratedContent] = useState<Partial<GeneratedContent>>(
    {} as Partial<GeneratedContent>
  );

  const { mutateAsync: contentGenerationDestroy } = useContentGenerationDelete({
    onSuccess: () => {
      handleScrollTop();
      toaster.success(t('messages.success.delete_content_generation'));
    },
    onError: (error: unknown) => {
      handleScrollTop();
      notifyException(error, ESpansException.ContentGenerationException);
      toaster.error(t('messages.error.delete_content_generation'));
    }
  });

  const [modalDeleteContentGenerationOpen, setModalDeleteContentGenerationOpen] = useState(false);

  const [contentId, setContentId] = useState('');

  const { data: nodesCount } = useContentGenerationNodesCount();

  const {
    control,
    data,
    refetch,
    isLoading,
    isPending,
    deferredSearchQuery,
    searchQuery,
    page,
    setPage,
    optionsSortAttribute,
    optionsSortOrder,
    optionsLanguages,
    optionsContentType,
    optionsContentFormat,
    handleClear,
    isContentFormatFilterDisabled
  } = useSearchParamsContentGeneration();

  const handleOpenDeleteContentGenerationModal = (contentId: string) => {
    setModalDeleteContentGenerationOpen(true);
    setContentId(contentId);
  };

  const handleOpenEditContentGenerationModal = useCallback(
    (contentId: string) => router.push(`/content-creator/${contentId}`),
    [router]
  );

  const handleCloseDeleteContentGenerationModal = () => {
    setContentId('');
    setModalDeleteContentGenerationOpen(false);
  };

  const handleDeleteContentGeneration = useCallback(async () => {
    await contentGenerationDestroy({
      generatedContentId: contentId
    });
    await refetch();
    setModalDeleteContentGenerationOpen(false);
  }, [contentId, contentGenerationDestroy, refetch]);

  const hasNodes = !!nodesCount;
  const isChecking = isLoading || isPending;
  const isSelectDisabled = !hasNodes;

  const onFetch = useCallback(async () => {
    if (!isChecking) {
      if (!!hasNodes && pathname.match(/^(\/[a-z]{2})?\/content-creator\/?$/))
        return router.push('/content-creator/list');
      if (!hasNodes && !pathname.includes('/content-creator/new'))
        return router.push('/content-creator');
    }
  }, [hasNodes, isChecking, pathname, router]);

  useEffect(() => {
    onFetch();
  }, [onFetch]);

  return (
    <ContentGenerationListContext.Provider
      value={{
        copyGeneratedContent,
        setCopyGeneratedContent,
        deferredSearchQuery,
        searchQuery,
        page,
        setPage,
        contentId,
        data,
        nodesCount,
        refetch,
        isLoading,
        hasNodes,
        isChecking,
        isSelectDisabled,
        handleOpenDeleteContentGenerationModal,
        handleOpenEditContentGenerationModal,
        handleCloseDeleteContentGenerationModal,
        handleDeleteContentGeneration,
        handleClear,
        modalDeleteContentGenerationOpen,
        optionsSortAttribute,
        optionsSortOrder,
        optionsLanguages,
        optionsContentType,
        optionsContentFormat,
        control,
        isContentFormatFilterDisabled
      }}
    >
      {children}
    </ContentGenerationListContext.Provider>
  );
};

export { ContentGenerationListProvider, useContentGenerationListContext };
